import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  CreditCard, 
  Calendar, 
  DollarSign, 
  AlertCircle, 
  Check, 
  X, 
  Plus,
  Trash2,
  Shield,
  Clock,
  Receipt,
  Star,
  CheckCircle,
  Settings
} from 'lucide-react';
import { useUserData } from '../hooks/useUserData';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/api';
import PaymentModal from './PaymentModal';

const BillingPage = ({ onBack }) => {
  const { subscription, loading, refreshUserData } = useUserData();
  const { language, tr } = useLanguage();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Payment Methods State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [processingMethod, setProcessingMethod] = useState(null);

  useEffect(() => {
    fetchPaymentHistory();
    fetchPaymentMethods();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      const response = await apiService.payment.getPaymentHistory();
      if (response.data.success) {
        setPaymentHistory(response.data.history || []);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      setLoadingMethods(true);
      const response = await apiService.payment.getPaymentMethods();
      if (response.data.success) {
        setPaymentMethods(response.data.cards || []);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoadingMethods(false);
    }
  };

  const handleRemovePaymentMethod = async (methodId) => {
    if (!window.confirm(tr('Are you sure you want to remove this payment method?', 'Voulez-vous vraiment supprimer ce moyen de paiement ?'))) {
      return;
    }

    try {
      setProcessingMethod(methodId);
      const response = await apiService.payment.removePaymentMethod(methodId);
      
      if (response.data.success) {
        await fetchPaymentMethods(); // Refresh list
        alert(tr('Payment method removed successfully!', 'Moyen de paiement supprimé !'));
      } else {
        throw new Error(response.data.message || 'Failed to remove payment method');
      }
    } catch (error) {
      console.error('Error removing payment method:', error);
      alert(error.response?.data?.detail || error.message || 'Failed to remove payment method');
    } finally {
      setProcessingMethod(null);
    }
  };

  const handleSetDefaultPaymentMethod = async (methodId) => {
    try {
      setProcessingMethod(methodId);
      const response = await apiService.payment.setDefaultPaymentMethod(methodId);
      
      if (response.data.success) {
        await fetchPaymentMethods(); // Refresh list
        alert(tr('Default payment method updated!', 'Moyen de paiement par défaut mis à jour !'));
      } else {
        throw new Error(response.data.message || 'Failed to set default payment method');
      }
    } catch (error) {
      console.error('Error setting default payment method:', error);
      alert(error.response?.data?.detail || error.message || 'Failed to set default payment method');
    } finally {
      setProcessingMethod(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(tr('Are you sure you want to cancel your subscription? This action cannot be undone.', 'Voulez-vous vraiment annuler votre abonnement ? Cette action est irréversible.'))) {
      return;
    }

    setLoadingCancel(true);
    try {
      const response = await apiService.payment.cancelSubscription();
      
      if (response.data.success) {
        alert(tr('Subscription cancelled successfully!', 'Abonnement annulé avec succès !'));
        // Refresh user data after successful cancellation
        await refreshUserData();
        // Optionally reload to ensure all UI updates
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        throw new Error(response.data.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      
      // Better error handling with user-friendly messages
      let errorMessage = tr('Failed to cancel subscription. Please try again.', "Échec de l'annulation. Veuillez réessayer.");
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setLoadingCancel(false);
    }
  };

  const getPlanDetails = () => {
    if (!subscription) return null;

    const plan = subscription.whizz_plan;
    switch (plan) {
      case 'free':
        return {
          name: tr('Free Plan', 'Plan Gratuit'),
          price: '$0.00',
          billing: tr('Forever', 'Pour toujours'),
          stories: `${subscription.whizz_stories_used || 0}/${subscription.whizz_stories_limit || 3}`,
          color: 'bg-gray-100',
          textColor: 'text-gray-600'
        };
      case 'standard':
        return {
          name: tr('Standard Plan', 'Plan Standard'),
          price: '$9.99',
          billing: tr('per month', 'par mois'),
          stories: `${subscription.whizz_stories_used || 0}/${subscription.whizz_stories_limit || 50}`,
          color: 'bg-blue-100',
          textColor: 'text-blue-600'
        };
      case 'on_demand':
        return {
          name: tr('On Demand', 'À la demande'),
          price: '$0.49',
          billing: tr('per story', 'par histoire'),
          stories: tr('Unlimited', 'Illimité'),
          color: 'bg-yellow-100',
          textColor: 'text-yellow-600'
        };
      default:
        return {
          name: tr('Unknown Plan', 'Plan inconnu'),
          price: '-',
          billing: '-',
          stories: '-',
          color: 'bg-gray-100',
          textColor: 'text-gray-600'
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatAmount = (amount, currency = 'usd') => {
    if (typeof amount !== 'number') return '$0.00';
    return `$${amount.toFixed(2)}`;
  };

  const getTransactionIcon = (packageId, paymentStatus) => {
    if (paymentStatus === 'paid') {
      return <Check className="w-4 h-4 text-green-500" />;
    } else if (paymentStatus === 'failed') {
      return <X className="w-4 h-4 text-red-500" />;
    } else {
      return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getManageSubscriptionColors = () => {
    const plan = subscription?.whizz_plan;
    if (plan === 'on_demand') {
      return {
        cardBg: 'bg-gradient-to-br from-yellow-50 to-orange-50',
        cardBorder: 'border-yellow-100',
        circleBg: 'bg-yellow-100',
        iconBg: 'bg-yellow-500',
        buttonBg: 'bg-yellow-600 hover:bg-yellow-700'
      };
    }
    // Default blue colors
    return {
      cardBg: 'bg-gradient-to-br from-blue-50 to-indigo-50',
      cardBorder: 'border-blue-100',
      circleBg: 'bg-blue-100',
      iconBg: 'bg-blue-500',
      buttonBg: 'bg-blue-600 hover:bg-blue-700'
    };
  };

  const getTabColors = () => {
    const plan = subscription?.whizz_plan;
    if (plan === 'on_demand') {
      return {
        active: 'bg-yellow-100 text-yellow-600',
        inactive: 'text-gray-600 hover:bg-gray-100'
      };
    }
    // Default blue colors
    return {
      active: 'bg-blue-100 text-blue-600',
      inactive: 'text-gray-600 hover:bg-gray-100'
    };
  };

  const handleChangePlan = () => {
    // Direct to payment modal with "Keep the magic alive!"
    setShowPaymentModal(true);
  };

  const getTransactionDescription = (transaction) => {
    const packageId = transaction.package_id;
    if (packageId === 'story') {
      return tr('Single Story Purchase', "Achat d'une histoire");
    } else if (packageId === 'standard') {
      return tr('Standard Monthly Subscription', 'Abonnement mensuel Standard');
    }
    return transaction.description || tr('Payment', 'Paiement');
  };

  // Payment Method Card Component
  const PaymentMethodCard = ({ method }) => {
    const isProcessing = processingMethod === method.id;
    const isExpired = new Date() > new Date(method.exp_year, method.exp_month - 1);
    
    const getCardIcon = (brand) => {
      const brandClass = `w-8 h-6 rounded border text-white text-xs font-bold flex items-center justify-center`;
      switch (brand.toLowerCase()) {
        case 'visa':
          return <div className={`${brandClass} bg-blue-600`}>VISA</div>;
        case 'mastercard':
          return <div className={`${brandClass} bg-red-500`}>MC</div>;
        case 'amex':
          return <div className={`${brandClass} bg-green-600`}>AMEX</div>;
        default:
          return <CreditCard className="w-6 h-6 text-gray-500" />;
      }
    };

    return (
      <div className={`group relative overflow-hidden bg-gradient-to-r from-gray-50 to-white border-2 rounded-2xl p-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
        method.is_default ? 'border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50' : 'border-gray-200'
      } ${isExpired ? 'border-red-200 bg-gradient-to-r from-red-50 to-pink-50' : ''}`}>
        
        {/* Default Badge */}
        {method.is_default && (
          <div className="absolute top-2 right-2">
            <div className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {tr('Default', 'Par défaut')}
            </div>
          </div>
        )}
        
        {/* Expired Badge */}
        {isExpired && (
          <div className="absolute top-2 right-2">
            <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {tr('Expired', 'Expirée')}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            {/* Card Brand Icon */}
            <div className="flex-shrink-0">
              {getCardIcon(method.brand)}
            </div>
            
            {/* Card Details */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-lg font-semibold text-gray-800">
                  •••• {method.last4}
                </span>
                <span className={`text-sm font-medium ${isExpired ? 'text-red-600' : 'text-gray-600'}`}>
                  {String(method.exp_month).padStart(2, '0')}/{method.exp_year}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="capitalize">{method.funding}</span>
                <span>•</span>
                <span className="uppercase">{method.country}</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {!method.is_default && !isExpired && (
              <button
                onClick={() => handleSetDefaultPaymentMethod(method.id)}
                disabled={isProcessing}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                title={tr("Set as default", "Définir par défaut")}
              >
                {isProcessing ? (
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Settings className="w-4 h-4" />
                )}
              </button>
            )}
            
            <button
              onClick={() => handleRemovePaymentMethod(method.id)}
              disabled={isProcessing}
              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              title={tr("Remove payment method", "Supprimer le moyen de paiement")}
            >
              {isProcessing ? (
                <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const planDetails = getPlanDetails();
  const manageColors = getManageSubscriptionColors();
  const tabColors = getTabColors();

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 p-4 overflow-y-auto">
        <div className="max-w-md mx-auto h-full">
          <div className="bg-white rounded-3xl shadow-2xl p-8 h-full flex flex-col">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="h-32 bg-gray-200 rounded mb-6"></div>
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 p-4 overflow-y-auto">
      <div className="max-w-md mx-auto h-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden h-full flex flex-col">
          {/* Header */}
          <div className="bg-white p-6 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-3 mb-6">
              <button 
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{tr('Billing & Subscription', 'Facturation & Abonnement')}</h1>
                <p className="text-gray-500">{tr('Manage your subscription and payment methods', 'Gérez votre abonnement et vos moyens de paiement')}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'overview' 
                    ? tabColors.active
                    : tabColors.inactive
                }`}
              >
                {tr('Overview', 'Aperçu')}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'history' 
                    ? tabColors.active
                    : tabColors.inactive
                }`}
              >
                {tr('Payment History', 'Historique des paiements')}
              </button>
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
              {/* Current Plan */}
              {planDetails && (
                <div className={`p-6 rounded-2xl ${planDetails.color} border border-gray-100`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className={`text-xl font-bold ${planDetails.textColor}`}>
                        {planDetails.name}
                      </h3>
                      <p className="text-gray-600 font-medium">
                        {planDetails.price} {planDetails.billing}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${planDetails.textColor} bg-white/70 backdrop-blur-sm`}>
                        <Star className="w-4 h-4" />
                        {tr('Active', 'Actif')}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white/70 backdrop-blur-sm p-4 rounded-xl border border-white/50">
                      <div className="flex items-center gap-2 mb-2">
                        <Receipt className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-600">{tr('Stories Usage', 'Histoires utilisées')}</span>
                      </div>
                      <p className="text-lg font-semibold text-gray-800">
                        {planDetails.stories}
                      </p>
                    </div>
                    
                    {subscription?.subscription_start && (
                      <div className="bg-white/70 backdrop-blur-sm p-4 rounded-xl border border-white/50">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium text-gray-600">{tr('Started', 'Débuté le')}</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-800">
                          {formatDate(subscription.subscription_start)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Plan Management Actions */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">{tr('Manage Subscription', "Gérer l'abonnement")}</h3>
                
                <div className="grid gap-4">
                  {/* Upgrade/Change Plan Card */}
                  <div className={`group relative overflow-hidden ${manageColors.cardBg} border ${manageColors.cardBorder} rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                    <div className={`absolute top-0 right-0 w-20 h-20 ${manageColors.circleBg} rounded-full -translate-y-10 translate-x-10 opacity-20`}></div>
                    <div className="relative">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 ${manageColors.iconBg} rounded-xl flex items-center justify-center shadow-lg`}>
                          <Star className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-800 text-lg mb-2">
                            {subscription?.whizz_plan === 'free' ? tr('Upgrade Plan', 'Améliorer le plan') : tr('Change Plan', 'Changer de plan')}
                          </h4>
                          <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                            {subscription?.whizz_plan === 'free' 
                              ? tr('Unlock unlimited stories and premium features with our Standard plan', 'Débloquez des histoires illimitées et des fonctionnalités premium avec notre plan Standard')
                              : tr('Switch between plans anytime to fit your needs', 'Changez de plan à tout moment selon vos besoins')
                            }
                          </p>
                          <button 
                            onClick={() => setShowPaymentModal(true)}
                            className={`inline-flex items-center gap-2 ${manageColors.buttonBg} text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200`}
                          >
                            <Star className="w-4 h-4" />
                            {subscription?.whizz_plan === 'free' ? tr('Upgrade Now', 'Améliorer maintenant') : tr('Change Plan', 'Changer de plan')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cancel Subscription Card (only for Standard plan) */}
                  {subscription?.whizz_plan === 'standard' && (
                    <div className="group relative overflow-hidden bg-gradient-to-br from-red-50 to-pink-50 border border-red-100 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-red-100 rounded-full -translate-y-10 translate-x-10 opacity-20"></div>
                      <div className="relative">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center shadow-lg">
                            <X className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-800 text-lg mb-2">{tr('Cancel Subscription', "Annuler l'abonnement")}</h4>
                            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                              {tr("Cancel your subscription anytime. You'll keep access to all features until the end of your current billing period.", "Annulez votre abonnement à tout moment. Vous conserverez l'accès à toutes les fonctionnalités jusqu'à la fin de votre période de facturation.")}
                            </p>
                            <button 
                              onClick={handleCancelSubscription}
                              disabled={loadingCancel}
                              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {loadingCancel ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  {tr('Canceling...', 'Annulation...')}
                                </>
                              ) : (
                                <>
                                  <X className="w-4 h-4" />
                                  {tr('Cancel Subscription', "Annuler l'abonnement")}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Methods */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">{tr('Payment Methods', 'Moyens de paiement')}</h3>
                
                {/* Security Notice */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100 rounded-2xl p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800 mb-1">{tr('Secure Payment Processing', 'Paiement sécurisé')}</p>
                      <p className="text-xs text-gray-600">{tr('Powered by Stripe. Your payment information is encrypted and secure.', 'Propulsé par Stripe. Vos informations de paiement sont chiffrées et sécurisées.')}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Methods List */}
                {loadingMethods ? (
                  <div className="space-y-3">
                    {[...Array(2)].map((_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-20 bg-gray-200 rounded-xl"></div>
                      </div>
                    ))}
                  </div>
                ) : paymentMethods.length > 0 ? (
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <PaymentMethodCard key={method.id} method={method} />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Payment Modal */}
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={() => {
              setShowPaymentModal(false);
              refreshUserData();
            }}
          />

          {/* Payment History Tab */}
          {activeTab === 'history' && (
            <div className="p-6 flex-1 overflow-y-auto">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{tr('Payment History', 'Historique des paiements')}</h3>
                <p className="text-gray-600">{tr('View all your past transactions and purchases', 'Consultez toutes vos transactions et achats passés')}</p>
              </div>

              {loadingHistory ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-16 bg-gray-200 rounded-xl"></div>
                    </div>
                  ))}
                </div>
              ) : paymentHistory.length > 0 ? (
                <div className="space-y-3">
                  {paymentHistory.map((transaction, index) => (
                    <div key={transaction.session_id || index} className="border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getTransactionIcon(transaction.package_id, transaction.payment_status)}
                          <div>
                            <h4 className="font-medium text-gray-800">
                              {getTransactionDescription(transaction)}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {formatDate(transaction.created_at)} • {transaction.payment_status || 'pending'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-800">
                            {formatAmount(transaction.amount, transaction.currency)}
                          </p>
                          <p className="text-xs text-gray-500 uppercase">
                            {transaction.currency || 'USD'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-800 mb-2">{tr('No payment history', 'Aucun historique de paiement')}</h4>
                  <p className="text-gray-600">{tr('Your payment transactions will appear here', 'Vos transactions apparaîtront ici')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingPage;